import { useRef } from "react";
import * as yup from 'yup';
import { Formik } from 'formik';
import { Button as BSButton, Form } from "react-bootstrap";
import { Button, FormSectionHeader, SlideOutModal, SlideOutModalProps } from "../../../components";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Popup, PopupProps } from "@progress/kendo-react-popup";

export interface AddClosureModalProps extends SlideOutModalProps {
  onSubmit: any;
}

export function AddClosureModal(props: Readonly<AddClosureModalProps>) {

  const submitRef = useRef<HTMLButtonElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const ModalPopup = (props: PopupProps) => (
    <Popup {...props} appendTo={modalBodyRef.current} />
  );

  const validationSchema = yup.object().shape({
    start: yup.date().required("Start date is required"),
    end: yup.date().required("End date is required").min(yup.ref('start'), "End date must be later than start date"),
  });

  return (
    <SlideOutModal
      {...props}
      title="Add Closure Period"
      modalBodyRef={modalBodyRef}
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100">
                Add
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          start: null,
          end: null,
        }}
        onSubmit={async (values) => {
          props.onSubmit(values);
        }}
      >
        {({ values, handleSubmit, setFieldValue, touched, errors }) => (
          <Form onSubmit={handleSubmit} className="px-2 pt-2">
            <div className="mb-4">
              <FormSectionHeader>Date Range</FormSectionHeader>

              <Form.Group className="mb-3">
                <Form.Label className="small text-body-secondary mb-1">Start Date</Form.Label>
                <DatePicker
                  value={values.start}
                  onChange={(e) => {
                    setFieldValue("start", e.value);
                  }}
                  popup={ModalPopup}
                  className="w-100"
                />
                {touched.start && typeof errors.start === "string" && (
                  <div className="text-danger mt-1 small">{errors.start}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small text-body-secondary mb-1">End Date</Form.Label>
                <DatePicker
                  value={values.end}
                  onChange={(e) => {
                    setFieldValue("end", e.value);
                  }}
                  popup={ModalPopup}
                  className="w-100"
                  min={values.start || undefined}
                />
                {touched.end && typeof errors.end === "string" && (
                  <div className="text-danger mt-1 small">{errors.end}</div>
                )}
              </Form.Group>

              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted">
                  <strong>Note:</strong> This will block all appointments during the selected period.
                </small>
              </div>
            </div>

            <BSButton ref={submitRef} type="submit" className="d-none" />
          </Form>
        )}
      </Formik>
    </SlideOutModal>
  );
}

export default AddClosureModal;